import { useState, useEffect, useCallback } from "react";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { usePermissions } from "@/contexts/PermissionsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Copy, GitBranch, Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import PipelineStagesEditor, {
  PipelineStagesSummary,
  STAGE_UAT,
  cleanStagesForSave,
  copyStages,
  firstPipelineError,
} from "@/components/deployment/PipelineStagesEditor";

// Mirrors MAX_TEMPLATES / MAX_NAME_LENGTH in services/deployment_pipeline.py.
const MAX_TEMPLATES = 25;
const MAX_TEMPLATE_NAME_LENGTH = 60;

// The threshold and interval fields PUT /deployment-automation/settings
// rewrites on every call. The pipelines card sends the server's current
// values back unchanged, so saving a template never moves a threshold.
const AUTOMATION_FIELDS = [
  "auto_progress_enabled",
  "test_to_phase1_threshold",
  "phase1_to_phase2_threshold",
  "phase2_to_phase3_threshold",
  "check_interval_minutes",
];

const pickAutomationFields = (settings) =>
  AUTOMATION_FIELDS.reduce((acc, key) => {
    if (settings && settings[key] !== undefined && settings[key] !== null) acc[key] = settings[key];
    return acc;
  }, {});

/**
 * Settings > Automation > Deployment pipelines.
 *
 * Lists the tenant's stage templates (the built-in, read-only one first, as
 * the server sends it), lets a user with deploy_packages add, edit,
 * duplicate and delete custom templates and choose the default. Each change
 * is saved straight away through PUT /deployment-automation/settings, so a
 * 400 (bad stage list, group not found in Entra) is reported against the
 * dialog that caused it.
 */
export default function DeploymentPipelinesCard({ getAuthHeaders }) {
  const { canDeployPackages } = usePermissions();
  const [templates, setTemplates] = useState([]);
  const [defaultTemplateId, setDefaultTemplateId] = useState(null);
  const [defaultThresholds, setDefaultThresholds] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // { originalId: string|null, name, stages } while the dialog is open.
  const [editing, setEditing] = useState(null);

  const applySettings = (data) => {
    const list = Array.isArray(data?.stage_templates) ? data.stage_templates : [];
    setTemplates(list);
    setDefaultTemplateId(data?.default_stage_template_id || list[0]?.id || null);
    setDefaultThresholds({
      first: data?.phase1_to_phase2_threshold,
      later: data?.phase2_to_phase3_threshold,
    });
  };

  const fetchSettings = useCallback(async () => {
    const headers = await getAuthHeaders();
    const response = await axios.get(`${API}/deployment-automation/settings`, { headers });
    return response.data;
  }, [getAuthHeaders]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchSettings();
        if (!cancelled) applySettings(data);
      } catch (error) {
        console.error("Failed to fetch deployment pipelines:", error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // fetchSettings changes identity on every SettingsPage render; load once.
    // eslint-disable-next-line -- the build loads no react-hooks plugin, so name no rule
  }, []);

  const customTemplates = templates.filter((t) => !t.built_in);
  const builtInTemplate = templates.find((t) => t.built_in) || null;
  const defaultTemplate = templates.find((t) => t.id === defaultTemplateId) || builtInTemplate;

  /**
   * Save the custom templates and default. Re-reads the server's settings
   * first and sends its thresholds back unchanged: the Deployment
   * Automation card above may have saved new ones since this card loaded.
   */
  const persist = async (nextCustomTemplates, nextDefaultId, successMessage) => {
    setSaving(true);
    try {
      const current = await fetchSettings();
      const headers = await getAuthHeaders();
      await axios.put(
        `${API}/deployment-automation/settings`,
        {
          ...pickAutomationFields(current),
          stage_templates: nextCustomTemplates.map((t) => ({
            ...(t.id ? { id: t.id } : {}),
            name: t.name,
            stages: cleanStagesForSave(t.stages),
          })),
          default_stage_template_id: nextDefaultId,
        },
        { headers }
      );
      // Server-normalised templates (ids for new ones, default names filled).
      applySettings(await fetchSettings());
      toast.success(successMessage);
      return true;
    } catch (error) {
      console.error("Failed to save deployment pipelines:", error);
      toast.error(error.response?.data?.detail || "Failed to save deployment pipelines");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const openNew = () => {
    // Start from the built-in template's stages: a valid list to adapt.
    setEditing({
      originalId: null,
      name: "",
      stages: copyStages(builtInTemplate?.stages || [{ id: "uat", type: STAGE_UAT, name: "UAT" }]),
    });
  };

  const openEdit = (template) => {
    setEditing({ originalId: template.id, name: template.name, stages: copyStages(template.stages) });
  };

  const openDuplicate = (template) => {
    setEditing({
      originalId: null,
      name: `Copy of ${template.name}`.slice(0, MAX_TEMPLATE_NAME_LENGTH),
      stages: copyStages(template.stages),
    });
  };

  const nameError = (() => {
    if (!editing) return null;
    const name = editing.name.trim();
    if (!name) return "Enter a name for the template.";
    const clash = templates.find(
      (t) => t.id !== editing.originalId && t.name.trim().toLowerCase() === name.toLowerCase()
    );
    return clash ? `There is already a template called '${clash.name}'.` : null;
  })();

  const saveEditing = async () => {
    if (!editing) return;
    const problem = nameError || firstPipelineError(editing.stages);
    if (problem) {
      toast.error(problem);
      return;
    }
    const edited = { id: editing.originalId, name: editing.name.trim(), stages: editing.stages };
    const next = editing.originalId
      ? customTemplates.map((t) => (t.id === editing.originalId ? edited : t))
      : [...customTemplates, edited];
    const ok = await persist(
      next,
      defaultTemplateId,
      editing.originalId ? `Template '${edited.name}' saved` : `Template '${edited.name}' added`
    );
    if (ok) setEditing(null);
  };

  const deleteTemplate = async (template) => {
    if (
      !window.confirm(
        `Delete the template '${template.name}'?\n\nApps set to use it will fall back to the tenant default pipeline for their next rollout. Rollouts already under way keep the stages they started with.`
      )
    ) {
      return;
    }
    const next = customTemplates.filter((t) => t.id !== template.id);
    const nextDefault =
      defaultTemplateId === template.id ? builtInTemplate?.id || null : defaultTemplateId;
    await persist(next, nextDefault, `Template '${template.name}' deleted`);
  };

  const changeDefault = async (templateId) => {
    if (templateId === defaultTemplateId) return;
    const chosen = templates.find((t) => t.id === templateId);
    await persist(customTemplates, templateId, `Default pipeline set to '${chosen?.name || templateId}'`);
  };

  return (
    <Card data-testid="deployment-pipelines-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 font-['Chivo']">
          <GitBranch className="h-5 w-5" />
          Deployment pipelines
        </CardTitle>
        <CardDescription>
          The stages a rollout passes through after packaging. Apps use the default unless their
          governance page chooses another template or a custom pipeline. A change applies to the
          next rollout; rollouts already under way keep their stages.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-white/70">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading pipelines...
          </div>
        ) : (
          <>
            <div className="space-y-2 max-w-md">
              <Label htmlFor="default-pipeline-select">Default pipeline</Label>
              {canDeployPackages ? (
                <Select
                  value={defaultTemplate?.id || ""}
                  onValueChange={changeDefault}
                  disabled={saving}
                >
                  <SelectTrigger id="default-pipeline-select" data-testid="default-pipeline-select">
                    <SelectValue placeholder="Choose a template" />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map((t) => (
                      <SelectItem key={t.id} value={t.id} data-testid={`default-pipeline-option-${t.id}`}>
                        {t.name}
                        {t.built_in ? " (built in)" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-sm text-white" data-testid="default-pipeline-readonly">
                  {defaultTemplate?.name || "Not set"}
                </p>
              )}
              <p className="text-xs text-white/60">
                Used by every app whose governance page is set to the tenant default.
              </p>
            </div>

            <div className="space-y-3" data-testid="pipeline-template-list">
              {templates.map((template) => {
                const isDefault = template.id === defaultTemplate?.id;
                return (
                  <div
                    key={template.id}
                    className="rounded-lg border border-white/15 bg-white/5 p-3 space-y-2"
                    data-testid={`pipeline-template-${template.id}`}
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium text-white">{template.name}</span>
                        {template.built_in && (
                          <Badge variant="outline" className="border-white/20 text-white/80">
                            Built in, read-only
                          </Badge>
                        )}
                        {isDefault && (
                          <span className="rounded-md bg-blue-500/20 px-2 py-0.5 text-xs text-blue-300">
                            Default
                          </span>
                        )}
                      </div>
                      {canDeployPackages && (
                        <div className="flex items-center gap-1">
                          {!template.built_in && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEdit(template)}
                              disabled={saving}
                              data-testid={`pipeline-template-edit-${template.id}`}
                            >
                              <Pencil className="h-4 w-4 mr-1" />
                              Edit
                            </Button>
                          )}
                          {customTemplates.length < MAX_TEMPLATES && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openDuplicate(template)}
                              disabled={saving}
                              data-testid={`pipeline-template-duplicate-${template.id}`}
                            >
                              <Copy className="h-4 w-4 mr-1" />
                              Duplicate
                            </Button>
                          )}
                          {!template.built_in && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="hover:text-red-300"
                              onClick={() => deleteTemplate(template)}
                              disabled={saving}
                              data-testid={`pipeline-template-delete-${template.id}`}
                            >
                              <Trash2 className="h-4 w-4 mr-1" />
                              Delete
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                    <PipelineStagesSummary
                      stages={template.stages}
                      testId={`pipeline-template-summary-${template.id}`}
                    />
                  </div>
                );
              })}
            </div>

            {canDeployPackages && customTemplates.length < MAX_TEMPLATES && (
              <Button onClick={openNew} disabled={saving} data-testid="pipeline-template-add-btn">
                <Plus className="h-4 w-4 mr-2" />
                New template
              </Button>
            )}
            {canDeployPackages && customTemplates.length >= MAX_TEMPLATES && (
              <p className="text-xs text-white/60">
                A tenant can have at most {MAX_TEMPLATES} templates. Delete one to add another.
              </p>
            )}
            {!canDeployPackages && (
              <p className="text-xs text-white/60" data-testid="pipelines-readonly-note">
                You need permission to deploy packages to change deployment pipelines.
              </p>
            )}
          </>
        )}
      </CardContent>

      <Dialog open={!!editing} onOpenChange={(open) => !open && !saving && setEditing(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="pipeline-template-dialog">
          <DialogHeader>
            <DialogTitle>{editing?.originalId ? "Edit template" : "New template"}</DialogTitle>
            <DialogDescription>
              UAT is always included. Named groups must already exist in Entra ID; ALICE checks
              them when you save.
            </DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="pipeline-template-name">Template name</Label>
                <Input
                  id="pipeline-template-name"
                  value={editing.name}
                  maxLength={MAX_TEMPLATE_NAME_LENGTH}
                  onChange={(e) => setEditing((prev) => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g. Core apps with Pilot"
                  className="mt-1"
                  data-testid="pipeline-template-name-input"
                />
                {nameError && editing.name !== "" && (
                  <p className="mt-1 text-xs text-red-300">{nameError}</p>
                )}
              </div>
              <div>
                <Label className="mb-2 block">Preview</Label>
                <PipelineStagesSummary stages={editing.stages} testId="pipeline-template-dialog-summary" />
              </div>
              <PipelineStagesEditor
                stages={editing.stages}
                onChange={(stages) => setEditing((prev) => ({ ...prev, stages }))}
                defaultThresholds={defaultThresholds}
                testIdPrefix="pipeline-template-editor"
              />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={saveEditing} disabled={saving} data-testid="pipeline-template-save-btn">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
